/* eslint-disable camelcase */
import React, { useContext, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";
import { makeStyles } from "@material-ui/core";

import {
  useStatus,
  usePriorities,
  useSeverity,
  populateMasterData,
  useIncident,
  Incident as Inc,
  Severity,
  Priority,
  Status,
  useIncidentTypes,
  IncidentType,
} from "lib/api";
import { IncidentContext, IncidentProvider } from "lib/context/IncidentContext";

import Container from "components/layout/Container";
import Header from "components/common/Header";
import IncidentDetails from "components/map/IncidentDetails";

import { Icon, OverflowTooltip } from "components/common";
import IncidentLocation from "components/map/IncidentLocation";
import IncidentSops from "components/map/IncidentSops";
import IncidentEvents from "components/map/IncidentEvents";
import IncidentCameras from "components/map/IncidentCameras";
import NotFound from "components/common/NotFound";
import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import useStyles from "./styles";

interface InnerIncidentProps {
  sections: { label: string; value: string }[];
  incident?: Inc;
  type?: IncidentType;
  priority?: Priority;
  severity?: Severity;
  status?: Status;
}

const InnerIncident: React.FC<InnerIncidentProps> = ({
  sections,
  incident: i,
  type: t,
  priority: p,
  severity: sev,
  status: st,
}) => {
  const classes = useStyles();

  const {
    incident,
    setIncident,
    type,
    setType,
    priority,
    setPriority,
    severity,
    setSeverity,
    status,
    setStatus,
  } = useContext(IncidentContext);

  useEffect(() => i && setIncident(i), [i, setIncident]);
  useEffect(() => t && setType(t), [t, setType]);
  useEffect(() => p && setPriority(p), [p, setPriority]);
  useEffect(() => sev && setSeverity(sev), [sev, setSeverity]);
  useEffect(() => st && setStatus(st), [st, setStatus]);

  if (!incident || !type || !priority || !severity || !status) return <></>;

  return (
    <>
      <HvGrid container spacing={0}>
        <HvGrid
          className={classes.sectionMargin}
          tabIndex={-1}
          id={sections[0].value}
          xs={12}
          item
        >
          <IncidentDetails />
        </HvGrid>
      </HvGrid>
      <HvGrid container spacing={0}>
        <HvGrid
          className={classes.sectionMargin}
          tabIndex={-1}
          id={sections[1].value}
          xs={12}
          item
        >
          <IncidentLocation />
        </HvGrid>
      </HvGrid>
      <HvGrid container spacing={0}>
        <HvGrid
          className={classes.sectionMargin}
          tabIndex={-1}
          id={sections[2].value}
          xs={12}
          item
        >
          <IncidentSops />
        </HvGrid>
      </HvGrid>
      <HvGrid container spacing={0}>
        <HvGrid
          className={classes.sectionMargin}
          tabIndex={-1}
          id={sections[3].value}
          xs={12}
          item
        >
          <IncidentEvents />
        </HvGrid>
      </HvGrid>
      <HvGrid container spacing={0}>
        <HvGrid
          className={classes.sectionMargin}
          tabIndex={-1}
          id={sections[4].value}
          xs={12}
          item
        >
          <IncidentCameras />
        </HvGrid>
      </HvGrid>
    </>
  );
};

const Incident: React.FC = () => {
  const { t } = useTranslation("map");
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const classes = useStyles();

  const { data: incidentData } = useIncident(id, true);

  const {
    inc_name,
    inc_nearest_camera_ids,
    inc_sop_map = [],
    hv_correlated_entities,
    fk_inc_type_id,
    fk_inc_priority_id,
    fk_inc_severity_id,
    fk_inc_status_id,
    appearance,
  } = incidentData?.data || {};

  const sections = [
    { label: t("sections.details.scrollToTitle"), value: "details" },
    { label: t("sections.location.scrollToTitle"), value: "location" },
    {
      label: t("sections.sop.scrollToTitle", {
        count: inc_sop_map.length || 0,
      }),
      value: "sop",
    },
    {
      label: t("sections.events.scrollToTitle", {
        count: hv_correlated_entities?.length || 0,
      }),
      value: "events",
    },
    {
      label: t("sections.cameras.scrollToTitle", {
        count: inc_nearest_camera_ids?.length || 0,
      }),
      value: "cameras",
    },
  ];

  const goBackHandler = () => {
    history.goBack();
  };

  const { data: incidentTypesData } = useIncidentTypes(undefined);

  const { data: statusData, mutate: revalidateStatus } = useStatus({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: priorityData, mutate: revalidatePriority } = usePriorities({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: severityData, mutate: revalidateSeverity } = useSeverity({
    revalidateOnFocus: false,
    suspense: true,
  });

  const calledPopulateMasterData = useRef(false);

  const noStaticData =
    statusData?.data?.length === 0 &&
    priorityData?.data?.length === 0 &&
    severityData?.data?.length === 0;

  useEffect(() => {
    if (noStaticData && !calledPopulateMasterData.current) {
      calledPopulateMasterData.current = true;

      (async () => {
        await populateMasterData();

        await Promise.all([
          revalidateStatus(),
          revalidatePriority(),
          revalidateSeverity(),
        ]);
      })();
    }
  }, [noStaticData, revalidatePriority, revalidateSeverity, revalidateStatus]);

  const innerClasses = useMemo(
    () =>
      makeStyles(() => ({
        background: {
          backgroundColor: appearance?.color,
          "& svg": {
            color: "white",
          },
        },
      })),
    [appearance]
  )();

  const incident = incidentData?.data;
  const incidentType = useMemo(
    () =>
      incidentTypesData?.data.find(
        ({ pk_inc_type_id }) => pk_inc_type_id === fk_inc_type_id
      ),
    [fk_inc_type_id, incidentTypesData?.data]
  );

  const severity = useMemo(
    () =>
      severityData?.data.find(
        ({ pk_svrt_id }) => pk_svrt_id === fk_inc_severity_id
      ),
    [fk_inc_severity_id, severityData?.data]
  );

  const priority = useMemo(
    () =>
      priorityData?.data.find(
        ({ pk_prt_id }) => pk_prt_id === fk_inc_priority_id
      ),
    [fk_inc_priority_id, priorityData?.data]
  );

  const status = useMemo(
    () =>
      statusData?.data.find(
        ({ pk_status_id }) => pk_status_id === fk_inc_status_id
      ),
    [fk_inc_status_id, statusData?.data]
  );

  return (
    <Container className={classes.container}>
      <Header
        title={
          <>
            <div
              className={clsx(classes.formHeaderIcon, innerClasses.background)}
            >
              <Icon
                iconData={appearance || {}}
                {...(appearance &&
                  appearance?.icon_type === "fontawesome" && {
                    color: "#FFFFFF",
                  })}
              />
            </div>
            <OverflowTooltip
              data={inc_name as string}
              className={clsx(classes.title)}
            />
          </>
        }
        sections={sections}
        onCancel={goBackHandler}
      />
      <IncidentProvider>
        <InnerIncident
          sections={sections}
          incident={incident}
          type={incidentType}
          severity={severity}
          priority={priority}
          status={status}
        />
      </IncidentProvider>
    </Container>
  );
};
export const ListIncidentAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(
      ModuleNames.incidentManagement,
      ModulePolicyTypes.incident
    );
  }, [hasModulePolicy]);
  return <>{hasRight ? <Incident /> : <NotFound />}</>;
};
export default Incident;
